'use client';

import React, { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SortDropdown } from 'src/components/Maps/ResultsList/SortDropdown';
import { getSortableDisplayOptions } from 'src/utils/strategyHelpers';
import type { StrategyMetric } from 'src/api/redux/strategy/types';
import type { StrategySortValues } from 'src/hooks/strategy/useStrategyMetricSort';
import {
  BASE_SORT_GROUP_ID,
  DATE_ADDED_SORT_OPTIONS,
  NAME_SORT_OPTIONS,
} from './constants';
import type {
  StrategySortDropdownProps,
  StrategySortGroup,
  StrategySortOption,
} from './types';

/**
 * Builds the sort groups for the map results: one group per metric with a
 * Highest/Lowest option per display option (as the Discovery AI-search sort does),
 * then name and date added. With several chains selected, metrics don't align across
 * strategies and `added_at` is per chain, so only the name options remain.
 */
export const buildStrategySortGroups = (
  strategyMetrics: StrategyMetric[] | undefined,
  showEncountersTotals: boolean,
  isMultiChain: boolean
): StrategySortGroup[] => {
  if (isMultiChain) {
    return [{ id: BASE_SORT_GROUP_ID, label: null, options: NAME_SORT_OPTIONS }];
  }

  const metricGroups = (strategyMetrics ?? []).flatMap((metric) => {
    if (!metric?.id || !metric.label) return [];
    // Never offer a standalone "Encounters" sort; the claims option is relabeled
    // "Encounters" when the flag is on and keeps its claims code as its identity.
    const displayOptions = getSortableDisplayOptions(
      metric.display_options,
      showEncountersTotals
    );
    if (!displayOptions.length) return [];

    const options = displayOptions.flatMap<StrategySortOption>((option) => [
      {
        key: `${option.code}-desc`,
        label: `Highest ${metric.label} ${option.label}`,
        optionLabel: `Highest ${option.label}`,
        sort: 'metric',
        order: 'desc',
        sort_metric: option.code,
      },
      {
        key: `${option.code}-asc`,
        label: `Lowest ${metric.label} ${option.label}`,
        optionLabel: `Lowest ${option.label}`,
        sort: 'metric',
        order: 'asc',
        sort_metric: option.code,
      },
    ]);
    return [{ id: metric.id.toString(), label: metric.label, options }];
  });

  return [
    ...metricGroups,
    {
      id: BASE_SORT_GROUP_ID,
      label: null,
      options: [...DATE_ADDED_SORT_OPTIONS, ...NAME_SORT_OPTIONS],
    },
  ];
};

const matchesSort = (option: StrategySortOption, value: StrategySortValues) =>
  option.sort === value.sort &&
  option.order === value.order &&
  (option.sort !== 'metric' || option.sort_metric === value.sort_metric);

const StrategySortDropdown = ({
  strategyMetrics,
  isMultiChain,
  value,
  onChange,
}: StrategySortDropdownProps) => {
  const { showEncountersTotals } = useFlags();

  const groups = useMemo(
    () =>
      buildStrategySortGroups(
        strategyMetrics,
        !!showEncountersTotals,
        isMultiChain
      ),
    [strategyMetrics, showEncountersTotals, isMultiChain]
  );

  const selectedKey = useMemo(() => {
    const allOptions = groups.flatMap((group) => group.options);
    return (
      allOptions.find((option) => matchesSort(option, value)) ?? allOptions[0]
    )?.key;
  }, [groups, value]);

  return (
    <SortDropdown
      groups={groups}
      selectedKey={selectedKey}
      onSelect={(option) =>
        onChange({
          sort: option.sort,
          order: option.order,
          sort_metric: option.sort_metric,
        })
      }
    />
  );
};

export default StrategySortDropdown;
